import { IGif } from '@giphy/js-types';
import { Grid, SearchBar, SearchContext, SearchContextManager } from '@giphy/react-components';
import { Box, Button, Tooltip } from '@televet/kibble-ui';
import GiphyBadge from 'assets/badges/Poweredby_640px-White_VertText.png';
import { CloseIcon } from 'assets/icons';
import { env } from 'env';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import { IKeyboardShortcutElementRefs } from 'pages/Conversations/interfaces/IKeyboardShortcutElementRefs';
import { convertFileToAttachment } from 'pages/Conversations/utils';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import styled from 'styled-components/macro';

interface IGiphyButtonProps {
  onGifSelection(file: IAttachmentOption): void;
  shortcuts: IKeyboardShortcutElementRefs;
}

const GiphyButton = ({ onGifSelection, shortcuts }: IGiphyButtonProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const onSelect = async (gif: IGif): Promise<void> => {
    const blob = await fetch(gif.images.downsized.url).then((r) => r.blob());
    const file = new File([blob], gif.title || 'Animated Gif', { type: 'image/gif' });
    const newAttachment = convertFileToAttachment(file);
    onGifSelection(newAttachment);
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDrawer = useCallback((): void => {
    trackEvent(ConversationEvents.ChannelGifsDrawerOpen);
    setIsDrawerOpen((prevState) => {
      return !prevState;
    });
  }, [trackEvent]);

  useEffect(() => {
    shortcuts.subscribe('g', toggleDrawer);
    return (): void => {
      shortcuts.unsubscribe('g', toggleDrawer);
    };
  }, [shortcuts, toggleDrawer]);

  return (
    <Tooltip label="Giphy">
      <Box>
        {isDrawerOpen && (
          <SearchContextManager apiKey={env.REACT_APP_GIPHY_KEY || ''} options={{ rating: 'pg' }}>
            <Drawer onSelect={onSelect} toggleDrawer={toggleDrawer} />
          </SearchContextManager>
        )}
        <Box
          onClick={(): void => {
            toggleDrawer();
          }}
        >
          <Button iconName="gif" variant="ghostNeutral" />
        </Box>
      </Box>
    </Tooltip>
  );
};

interface IDrawerProps {
  onSelect(gif: IGif): Promise<void>;
  toggleDrawer(): void;
}
const Drawer = ({ onSelect, toggleDrawer }: IDrawerProps): JSX.Element => {
  const { fetchGifs, term, channelSearch, activeChannel } = useContext(SearchContext);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const { trackEvent } = useTracking();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any): void {
      if (!drawerRef.current?.contains(event.target)) {
        toggleDrawer();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [drawerRef, toggleDrawer]);

  return (
    <DrawerWrapper ref={drawerRef}>
      <Header>
        <StyledCloseIcon onClick={toggleDrawer} />
      </Header>
      <StyledSearchBar autoFocus />
      <Results>
        {!term ? (
          <EmptySearchArea>
            <StyledBadge src={GiphyBadge} />
          </EmptySearchArea>
        ) : (
          <Grid
            key={`${channelSearch} ${term} ${activeChannel?.user.username}`}
            columns={600 < 400 ? 2 : 4}
            width={475}
            fetchGifs={fetchGifs}
            noLink
            hideAttribution
            onGifClick={(gif: IGif): void => {
              trackEvent(ConversationEvents.ChannelGifSelectedFromDrawer);
              onSelect(gif);
              toggleDrawer();
            }}
            noResultsMessage={term ? `Oops can't find a gif for ${term}` : ''}
          />
        )}
      </Results>
    </DrawerWrapper>
  );
};

export default GiphyButton;

const DrawerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 20px;
  right: 10px;
  height: 500px;
  width: 500px;
  border: solid 1px #d9d9d9;
  background-color: white;
  border-radius: 8px;
  padding: 2px;
  z-index: 99;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 10px;
`;
const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  height: 12px;
  width: 12px;
`;

const EmptySearchArea = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const StyledSearchBar = styled(SearchBar)`
  border: solid 1px #d9d9d9;
  margin: 0 15px;
`;

const StyledBadge = styled.img`
  width: 200px;
`;

const Results = styled.div`
  padding-top: 10px;
  overflow-y: auto;
  cursor: pointer;
  text-align: center;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`;
